/**
 * `/settings/*` — a layout route with the section nav, so every settings screen
 * shares one chrome and the sub-routes stay small.
 *
 * ## Mobile
 *
 * The 224px section column plus a settings form does not fit in 390px, and the
 * column is not the content — it is a table of contents somebody reads once.
 * Below `md` it becomes a horizontally scrolling strip of chips above the panel,
 * which keeps the whole width for the form and keeps every section one tap away.
 * Pure CSS: the same `<Link>`s, restyled, so there is no second nav to keep in
 * sync and no state to lose on rotation.
 */

import { cn } from '@crm/ui';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { useOrgListQuery } from '../../api/queries.ts';
import { PageHeader } from '../../components/page.tsx';
import { RouteErrorPanel } from '../../components/route-error-boundary.tsx';

/**
 * `adminOnly` gates a section on `isPlatformAdmin`, which rides on the org list
 * the shell already fetches — so the nav costs no extra request. It hides the
 * entry and nothing more: `/settings/users` re-checks the flag itself, because a
 * missing link has never stopped anyone pasting a URL.
 */
const SECTIONS: readonly {
  readonly to: string;
  readonly label: string;
  readonly adminOnly?: boolean;
}[] = [
  { to: '/settings/profile', label: 'Profile' },
  { to: '/settings/accounts', label: 'Connected accounts' },
  { to: '/settings/organization', label: 'Organization' },
  { to: '/settings/users', label: 'Users', adminOnly: true },
  { to: '/settings/sharing', label: 'Sharing' },
  { to: '/settings/privacy', label: 'Privacy & recording' },
  { to: '/settings/devices', label: 'Devices' },
];

export const Route = createFileRoute('/_authed/settings')({
  component: SettingsLayout,
  errorComponent: RouteErrorPanel,
});

function SettingsLayout() {
  const orgs = useOrgListQuery();
  const sections = SECTIONS.filter(
    (section) => section.adminOnly !== true || orgs.data?.isPlatformAdmin === true,
  );

  return (
    <>
      <PageHeader title="Settings" />
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <nav
          className={cn(
            'shrink-0 border-border p-3',
            // < md: a scrolling strip under the header.
            'overflow-x-auto border-b',
            // >= md: the original column.
            'md:w-56 md:overflow-visible md:border-b-0 md:border-r',
          )}
        >
          <ul className="flex gap-1 md:flex-col md:gap-0.5">
            {sections.map((section) => (
              <li key={section.to} className="shrink-0">
                <Link
                  to={section.to}
                  className={cn(
                    'flex items-center whitespace-nowrap rounded-md px-3 py-2 text-sm text-muted-foreground',
                    'hover:bg-accent hover:text-accent-foreground',
                    'border border-border md:border-0 md:px-2 md:py-1.5',
                    '[@media(pointer:coarse)]:min-h-11',
                  )}
                  activeProps={{
                    className: 'bg-accent text-accent-foreground font-medium border-accent',
                  }}
                >
                  {section.label}
                </Link>
              </li>
            ))}
          </ul>
          {/* Hermes and enrichment are in design §2.2's inventory but have no
              backing configuration yet; they are omitted rather than listed as
              dead links. Privacy landed with the recorder integration, because
              that is the track that gave the do-not-record flag a write path. */}
        </nav>
        <div className="min-h-0 min-w-0 flex-1 overflow-auto p-4">
          <Outlet />
        </div>
      </div>
    </>
  );
}
